import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Drawer } from '@components/ui/drawer/component';
import { useIsPhone } from '@hooks/useIsPhone';
import { DialogDescription } from '@radix-ui/react-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '..';
import { useDiscardGuard } from '@hooks/useDiscardGuard';
import { DiscardChanges } from '@components/ui/discardChanges';
import { ResponsiveDialogProps } from './types';

export const ResponsiveDialog = forwardRef<HTMLDivElement, ResponsiveDialogProps>(
  ({ open, setOpen, title, description, children, trigger, className, confirmClose }, ref) => {
    const isPhone = useIsPhone();
    const guard = useDiscardGuard(confirmClose, setOpen);

    if (isPhone)
      return (
        <>
          {trigger && <Slot onClick={() => setOpen(true)}>{trigger}</Slot>}
          <Drawer
            ref={ref}
            phoneSheet
            open={open}
            setOpen={setOpen}
            confirmClose={confirmClose}
            title={title}>
            <div className="scrollbar-thin min-h-0 overflow-y-auto overscroll-contain px-4 pt-4">
              {description && <p className="mb-4 text-sm text-neutral-600">{description}</p>}
              {children}
            </div>
          </Drawer>
        </>
      );

    return (
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent ref={ref} className={className}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          {children}
          <DiscardChanges open={guard.asking} onKeep={guard.keep} onDiscard={guard.discard} />
        </DialogContent>
      </Dialog>
    );
  },
);
